import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Post-Stripe-redirect landing page.
 *
 * Stripe redirects to `/checkout/sucesso?session_id=cs_test_...` after
 * payment. We poll `/api/payments/checkout/status/{id}` until status="paid"
 * (typically 1-3 polls) then auto-login the owner and ship them into /app.
 */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { completeAutoLogin } = useAuth();
  const [phase, setPhase] = useState("verifying");      // verifying|paid|failed|timeout
  const [credits, setCredits] = useState(0);
  const [secondsWaited, setSecondsWaited] = useState(0);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      return;
    }

    let stopped = false;
    // Stripe Brasil às vezes demora 30-120s pra reportar payment_status=paid
    // (captura assíncrona, 3DS, antifraude). 6 minutos cobre praticamente
    // qualquer cenário razoável sem prender o operador se algo der MUITO
    // errado. O backend nesse meio tempo tem 2 mecanismos paralelos:
    // webhook (idempotente) e o próprio polling chamando Stripe.retrieve
    // direto — quem ganhar a corrida ativa o tenant.
    const MAX_POLLS = 180;          // 180 × 2s = 360s = 6 min
    const POLL_INTERVAL_MS = 2000;
    const startTime = Date.now();
    const tickTimer = setInterval(() => {
      if (!stopped) setSecondsWaited(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    const poll = async () => {
      if (stopped) return;
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.status === "paid") {
          setCredits(data.credits_granted || 0);
          setPhase("paid");
          // For subscription_initial the backend mints a JWT — log in
          // automatically and redirect to the dashboard.
          if (data.auto_login_token) {
            try {
              // We need user+tenant to populate context. Use /auth/me with
              // the new token via a transient axios instance.
              const meResp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
                { headers: { Authorization: `Bearer ${data.auto_login_token}` } },
              );
              const me = await meResp.json();
              const tenantResp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/tenant`,
                { headers: { Authorization: `Bearer ${data.auto_login_token}` } },
              );
              const tenant = tenantResp.ok ? await tenantResp.json() : null;
              completeAutoLogin(data.auto_login_token, me, tenant);
              setTimeout(() => nav("/app"), 1500);
            } catch {
              setTimeout(() => nav("/login"), 1500);
            }
          } else {
            setTimeout(() => nav("/app/financeiro"), 1500);
          }
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setPhase("failed");
          return;
        }
        if (pollCount.current >= MAX_POLLS) {
          setPhase("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (pollCount.current >= MAX_POLLS) {
          setPhase("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { stopped = true; clearInterval(tickTimer); };
  }, [sessionId, nav, completeAutoLogin]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4" data-testid="checkout-success-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200/80 p-8 text-center">
        {phase === "verifying" && (
          <>
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-4">
              <span className="w-6 h-6 rounded-full border-[2.5px] border-slate-200 border-t-slate-900 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 mb-1">
              Confirmando pagamento…
            </h1>
            <p className="text-[13px] text-slate-500 leading-relaxed">
              Validando sua transação com o Stripe.
              {secondsWaited < 30
                ? " Isso costuma levar poucos segundos."
                : secondsWaited < 90
                ? " Quase lá — o Stripe está finalizando o processamento."
                : " Pagamentos no Brasil às vezes demoram alguns minutos. Não feche esta janela."}
            </p>
            {secondsWaited > 10 && (
              <p className="text-[11.5px] text-slate-400 mt-3 tabular-nums">
                {secondsWaited}s aguardando confirmação do banco
              </p>
            )}
            {secondsWaited > 60 && (
              <button
                onClick={() => nav("/login")}
                className="mt-5 w-full bg-slate-900 text-white text-[13px] font-medium py-2.5 rounded-xl hover:bg-slate-700 transition-colors"
                data-testid="checkout-success-escape-btn"
              >
                Já paguei — ir para o login
              </button>
            )}
          </>
        )}

        {phase === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4" data-testid="checkout-success-icon">
              <CheckCircle size={28} weight="fill" className="text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 mb-1">Tudo certo!</h1>
            <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
              Sua conta foi ativada com sucesso.
              {credits > 0 && (
                <>
                  {" "}<strong className="text-slate-900">{credits.toLocaleString("pt-BR")} créditos</strong> já foram adicionados.
                </>
              )}
            </p>
            <div className="text-[11.5px] text-slate-500 inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" />
              Levando você ao painel…
            </div>
          </>
        )}

        {phase === "failed" && (
          <>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">Pagamento não confirmado</h1>
            <p className="text-[13px] text-slate-500 leading-relaxed mb-5">
              Algo deu errado com a sua cobrança. Você pode tentar novamente sem perder os dados cadastrados.
            </p>
            <button
              onClick={() => nav("/checkout")}
              className="w-full bg-slate-900 text-white text-[14px] font-medium py-3 rounded-xl hover:bg-slate-700 transition-colors"
              data-testid="checkout-success-retry-btn"
            >
              Tentar novamente
            </button>
          </>
        )}

        {phase === "timeout" && (
          <>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">Sua compra está sendo finalizada</h1>
            <p className="text-[13px] text-slate-500 leading-relaxed mb-5">
              O Stripe está terminando de processar — em até 5 minutos sua conta estará ativa.
              Você pode fazer login normalmente assim que receber a confirmação no seu email.
            </p>
            <button
              onClick={() => nav("/login")}
              className="w-full bg-slate-900 text-white text-[14px] font-medium py-3 rounded-xl hover:bg-slate-700 transition-colors"
            >
              Ir para o login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
